// src/features/study/hooks/useArtifactExportActions.js
import { useCallback } from "react";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
} from "docx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { getArtifactTitle, markdownToHtml, formatSurveyClipboardContent } from "../../../utils/artifactUtils";

/**
 * StudyPage의 export/clipboard 관련 액션들을 한 곳으로 모은 훅
 * (복사, 다운로드: Word/PDF/Markdown, Google Forms 스크립트 생성)
 */
export function useArtifactExportActions({
  artifact,
  studyName,
  parseSurveyData,
  onCopySuccess,
  setGoogleFormsModal,
}) {
  const handleCopy = useCallback(async () => {
    if (artifact.artifact_type === "survey") {
      const surveyData = parseSurveyData(artifact.content);
      const { plain, html } = formatSurveyClipboardContent(surveyData);
      if (!plain) {
        alert("복사할 설문 문항이 없습니다.");
        return;
      }

      const htmlBlob = new Blob([html], { type: "text/html" });
      const plainBlob = new Blob([plain], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": htmlBlob, "text/plain": plainBlob }),
      ]);
    } else if (artifact.artifact_type === "plan" || artifact.artifact_type === "guideline") {
      // 서식 포함 복사: HTML + plain 동시에 넣어서 워드/한글 등에 붙여넣을 때 서식 유지
      const html = markdownToHtml(artifact.content || "");
      const plain = artifact.content || "";
      const htmlBlob = new Blob([html], { type: "text/html" });
      const plainBlob = new Blob([plain], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": htmlBlob, "text/plain": plainBlob }),
      ]);
    } else {
      navigator.clipboard.writeText(artifact.content);
    }

    onCopySuccess?.();
  }, [artifact, parseSurveyData, onCopySuccess]);

  const handleDownloadWord = useCallback(async () => {
    try {
      let contentToConvert = artifact.content;

      if (artifact.artifact_type === "survey") {
        const surveyData = parseSurveyData(artifact.content);
        if (surveyData && surveyData.questions) {
          contentToConvert = "# 설문 문항\n\n";
          surveyData.questions.forEach((q, index) => {
            contentToConvert += `## ${index + 1}. ${q.text}\n**유형**: ${q.type}\n\n`;
            if (q.options && q.options.length > 0) {
              contentToConvert += "**선택지**:\n";
              q.options.forEach((opt, optIdx) => {
                contentToConvert += `${optIdx + 1}. ${opt}\n`;
              });
            }
            contentToConvert += "\n---\n\n";
          });
        }
      }

      const lines = contentToConvert.split("\n");
      const children = [];
      let tableBuffer = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.trim().startsWith("|")) {
          tableBuffer.push(line);
          if (i < lines.length - 1 && !lines[i + 1].trim().startsWith("|")) {
            if (tableBuffer.length > 0) {
              const tableRows = tableBuffer.filter((l) => !l.includes("---"));
              const docxTableRows = tableRows.map((row) => {
                const cells = row
                  .split("|")
                  .filter((c) => c.trim())
                  .map((c) => c.trim());
                return new DocxTableRow({
                  children: cells.map(
                    (cellText) =>
                      new DocxTableCell({
                        children: [new Paragraph(cellText)],
                        width: {
                          size: 100 / cells.length,
                          type: WidthType.PERCENTAGE,
                        },
                      })
                  ),
                });
              });

              children.push(
                new DocxTable({
                  rows: docxTableRows,
                  width: { size: 100, type: WidthType.PERCENTAGE },
                })
              );

              tableBuffer = [];
            }
          }
          continue;
        }

        if (line.startsWith("# ")) {
          children.push(
            new Paragraph({
              text: line.replace("# ", ""),
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            })
          );
        } else if (line.startsWith("## ")) {
          children.push(
            new Paragraph({
              text: line.replace("## ", ""),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 150 },
            })
          );
        } else if (line.startsWith("### ")) {
          children.push(
            new Paragraph({
              text: line.replace("### ", ""),
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );
        } else if (line.startsWith("* ") || line.startsWith("- ")) {
          children.push(
            new Paragraph({
              text: line.replace(/^[\*\-] /, ""),
              bullet: { level: 0 },
              spacing: { before: 50, after: 50 },
            })
          );
        } else if (line.trim()) {
          const runs = [];
          const boldRegex = /\*\*(.*?)\*\*/g;
          let lastIndex = 0;
          let match;

          while ((match = boldRegex.exec(line)) !== null) {
            if (match.index > lastIndex) {
              runs.push(new TextRun(line.substring(lastIndex, match.index)));
            }
            runs.push(new TextRun({ text: match[1], bold: true }));
            lastIndex = match.index + match[0].length;
          }

          if (lastIndex < line.length) {
            runs.push(new TextRun(line.substring(lastIndex)));
          }

          children.push(
            new Paragraph({
              children: runs.length > 0 ? runs : [new TextRun(line)],
              spacing: { before: 50, after: 50 },
            })
          );
        }
      }

      const doc = new Document({
        sections: [{ properties: {}, children }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${getArtifactTitle(artifact.artifact_type) || "문서"}_${studyName}.docx`);
    } catch (error) {
      console.error("Word 문서 생성 실패:", error);
      alert("Word 문서 생성에 실패했습니다.");
    }
  }, [artifact, parseSurveyData, studyName]);

  const handleDownloadPDF = useCallback(async () => {
    try {
      // PDF는 html2canvas(스크린샷) 기반이라, 캡처 대상 DOM을 정확히 좁혀야 함.
      // - plan: 본문(Plan Tiptap viewer)만
      // - survey: 설문 폼 빌더 컨테이너
      // - 그 외: 기존처럼 카드 전체
      let element;
      if (artifact.artifact_type === "plan") {
        element = document.querySelector(".plan-tiptap-viewer");
      } else if (artifact.artifact_type === "survey") {
        element = document.querySelector(".survey-form-builder-container");
      } else {
        element = document.querySelector(".detail-content-card");
      }

      if (!element) {
        throw new Error("PDF로 저장할 영역을 찾을 수 없습니다.");
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${getArtifactTitle(artifact.artifact_type) || "문서"}_${studyName}.pdf`);
    } catch (error) {
      console.error("PDF 생성 실패:", error);
      alert("PDF 생성에 실패했습니다.");
    }
  }, [artifact, studyName]);

  const handleDownloadMarkdown = useCallback(() => {
    let contentToDownload = artifact.content;

    if (artifact.artifact_type === "survey") {
      const surveyData = parseSurveyData(artifact.content);
      if (surveyData && surveyData.questions) {
        contentToDownload = "📝 설문 문항\n\n";
        surveyData.questions.forEach((q, index) => {
          contentToDownload += `${index + 1}. [${q.type}] ${q.text}\n`;
          if (q.options && q.options.length > 0) {
            q.options.forEach((opt, optIdx) => {
              contentToDownload += `   ${optIdx + 1}. ${opt}\n`;
            });
          }
          contentToDownload += "\n";
        });
      }
    }

    const blob = new Blob([contentToDownload], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${getArtifactTitle(artifact.artifact_type) || "문서"}_${studyName}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [artifact, parseSurveyData, studyName]);

  const handleGenerateGoogleFormsScript = useCallback(() => {
    const surveyData = parseSurveyData(artifact.content);
    if (!surveyData || !surveyData.questions || surveyData.questions.length === 0) {
      alert("설문 문항이 없습니다.");
      return;
    }

    let script = `function createSurveyForm() {\n  // Google Forms 생성\n  var form = FormApp.create('${studyName} - 스크리너 설문');\n  \n  // 폼 설명 추가\n  form.setDescription('이 설문은 연구 참여자 선별을 위한 스크리닝 설문입니다.');\n  \n`;

    surveyData.questions.forEach((q, index) => {
      if (q.options && q.options.length > 0) {
        script += `  // 문항 ${index + 1}: ${q.text}\n  var question${index + 1} = form.addMultipleChoiceItem();\n  question${index + 1}.setTitle('${q.text.replace(/'/g, "\\'")}');\n  question${index + 1}.setChoices([\n`;
        q.options.forEach((opt, optIdx) => {
          script += `    question${index + 1}.createChoice('${opt.replace(/'/g, "\\'")}')${
            optIdx < q.options.length - 1 ? "," : ""
          }\n`;
        });
        script += `  ]);\n  question${index + 1}.setRequired(true);\n  \n`;
      } else {
        script += `  // 문항 ${index + 1}: ${q.text}\n  var question${index + 1} = form.addTextItem();\n  question${index + 1}.setTitle('${q.text.replace(/'/g, "\\'")}');\n  question${index + 1}.setRequired(true);\n  \n`;
      }
    });

    script += `  // 폼 URL 출력\n  Logger.log('생성된 폼 URL: ' + form.getPublishedUrl());\n  Logger.log('편집 URL: ' + form.getEditUrl());\n}`;

    setGoogleFormsModal({ isOpen: true, script, showToast: false });
  }, [artifact, parseSurveyData, setGoogleFormsModal, studyName]);

  return {
    handleCopy,
    handleDownloadWord,
    handleDownloadPDF,
    handleDownloadMarkdown,
    handleGenerateGoogleFormsScript,
  };
}
